import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

export interface StatusColorToken {
  main: string;
  text: string;
  bg: string;
  border?: string;
  borderColor?: string;
}

export interface DomainColorToken {
  main: string;
  light: string;
  dark?: string;
  darker?: string;
  lighter: string;
  contrastText: string;
  gradient: string;
  card: string;
  bg: string;
  text: string;
  tint: string;
  border: string;
  borderColor?: string;
}

export interface PmsTokens {
  stay: DomainColorToken;
  dayUse: DomainColorToken;
  reservationStatus: {
    requested: StatusColorToken;
    checkedIn: StatusColorToken;
    checkedOut: StatusColorToken;
    canceled: StatusColorToken;
    noShow: StatusColorToken;
  };
  unassigned: StatusColorToken;
  unclassified: StatusColorToken;
  housekeeping: StatusColorToken;
  inspected: StatusColorToken;
  stopSale: StatusColorToken;
}

declare module '@mui/material/styles' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter?: string;
    darker?: string;
  }
  interface PaletteColor {
    lighter?: string;
    darker?: string;
  }
  interface Palette {
    stay: typeof stay & {
      bg?: string;
      text?: string;
      tint?: string;
      border?: string;
    };
    dayUse: typeof dayUse & {
      bg?: string;
      text?: string;
      tint?: string;
      border?: string;
    };
    housekeeping: typeof housekeeping & {
      light?: string;
      bg?: string;
      border?: string;
    };
    inspected: typeof inspected & {
      light?: string;
      bg?: string;
      border?: string;
    };
    amenity: typeof amenity;
    additionPrice: typeof additionPrice;
    pms: PmsTokens;
  }
  interface PaletteOptions {
    stay?: typeof stay & {
      bg?: string;
      text?: string;
      tint?: string;
      border?: string;
    };
    dayUse?: typeof dayUse & {
      bg?: string;
      text?: string;
      tint?: string;
      border?: string;
    };
    housekeeping?: typeof housekeeping & {
      light?: string;
      bg?: string;
      border?: string;
    };
    inspected?: typeof inspected & {
      light?: string;
      bg?: string;
      border?: string;
    };
    amenity?: typeof amenity;
    additionPrice?: typeof additionPrice;
    pms?: PmsTokens;
  }
}

// SETUP COLORS

export const grey = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

export const primary = {
  lighter: '#C8FAD6',
  light: '#5BE49B',
  main: '#00A76F',
  dark: '#007867',
  darker: '#004B50',
  contrastText: '#FFFFFF',
};

export const secondary = {
  lighter: '#EFD6FF',
  light: '#C684FF',
  main: '#8E33FF',
  dark: '#5119B7',
  darker: '#27097A',
  contrastText: '#FFFFFF',
};

export const info = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

export const success = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#ffffff',
};

export const warning = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: grey[800],
};

export const error = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
};

export const common = {
  black: '#000000',
  white: '#FFFFFF',
};

export const action = {
  hover: alpha(grey[500], 0.08),
  selected: alpha(grey[500], 0.16),
  disabled: alpha(grey[500], 0.8),
  disabledBackground: alpha(grey[500], 0.24),
  focus: alpha(grey[500], 0.24),
  hoverOpacity: 0.08,
  disabledOpacity: 0.48,
};

export const stay = {
  lighter: '#A59EDB',
  light: '#7A72C2',
  main: '#9E5BFF',
  dark: '#3A3675',
  darker: '#27234F',
  contrastText: '#FFFFFF',
  gradient: `linear-gradient(90deg, #7E30FF 0%, #5F1EC3 100%)`,
  card: 'linear-gradient(96.34deg, #A670FF 0%, #9E5BFA 100%)',
};

export const dayUse = {
  lighter: '#FFE6B3',
  light: '#FFDB8A',
  main: '#FFAA01',
  dark: '#B78A3F',
  darker: '#7A5C29',
  contrastText: '#FFFFFF',
  gradient: `linear-gradient(90deg, #FFC75F 0%, #F16908 100%)`,
  card: 'linear-gradient(96.34deg, #FF9E4F 0%, #FD9738 100%)',
};

export const housekeeping = {
  main: '#36ADF9',
};

export const inspected = {
  main: '#FB619D',
};

export const amenity = {
  main: '#1ABC9C',
};

export const additionPrice = {
  main: '#D558FF',
};

export const stopSale = {
  main: grey[500],
  text: grey[600],
};

const base = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  grey,
  common,
  divider: alpha(grey[500], 0.2),
  action,
  stay,
  dayUse,
  housekeeping,
  inspected,
  amenity,
  additionPrice,
  stopSale,
};

const lightStay: DomainColorToken = {
  ...stay,
  bg: '#F3EEFF',
  text: '#9E5BFF',
  tint: alpha('#9E5BFF', 0.08),
  border: 'none',
  borderColor: alpha('#9E5BFF', 0.3),
};

const lightDayUse: DomainColorToken = {
  ...dayUse,
  bg: '#FFF5E9',
  text: '#FF9900',
  tint: alpha('#FF9900', 0.08),
  border: 'none',
  borderColor: alpha('#FD983E', 0.4),
};

const darkStay: DomainColorToken = {
  ...stay,
  light: '#B882FF',
  lighter: alpha('#9E5BFF', 0.2),
  bg: alpha('#9E5BFF', 0.2),
  text: '#B882FF',
  tint: alpha('#9E5BFF', 0.16),
  border: `1px solid ${alpha('#9E5BFF', 0.35)}`,
  borderColor: alpha('#9E5BFF', 0.35),
};

const darkDayUse: DomainColorToken = {
  ...dayUse,
  light: '#FFAE33',
  lighter: alpha('#FF9900', 0.2),
  bg: alpha('#FF9900', 0.2),
  text: '#FFAE33',
  tint: alpha('#FF9900', 0.16),
  border: `1px solid ${alpha('#FD983E', 0.35)}`,
  borderColor: alpha('#FD983E', 0.35),
};

export const lightPms: PmsTokens = {
  stay: lightStay,
  dayUse: lightDayUse,
  reservationStatus: {
    requested: {
      main: '#FFAB00',
      text: '#FFAB00',
      bg: '#FFAB0014',
      border: 'none',
      borderColor: 'transparent',
    },
    checkedIn: {
      main: '#138D58',
      text: '#138D58',
      bg: '#138D5814',
      border: 'none',
      borderColor: 'transparent',
    },
    checkedOut: {
      main: '#637381',
      text: '#637381',
      bg: '#63738114',
      border: 'none',
      borderColor: 'transparent',
    },
    canceled: {
      main: '#FF6D62',
      text: '#FF6D62',
      bg: '#FF6D6214',
      border: 'none',
      borderColor: 'transparent',
    },
    noShow: {
      main: '#606362',
      text: '#606362',
      bg: '#60636214',
      border: 'none',
      borderColor: 'transparent',
    },
  },
  unassigned: {
    main: '#FF5730',
    text: '#FF5730',
    bg: '#FF57301A',
    border: 'none',
    borderColor: 'transparent',
  },
  unclassified: {
    main: '#FF3A1F',
    text: '#FF3A1F',
    bg: alpha('#FF3A1F', 0.02),
    border: 'none',
    borderColor: 'transparent',
  },
  housekeeping: {
    main: '#36ADF9',
    text: '#36ADF9',
    bg: alpha('#36ADF9', 0.05),
    border: `1px solid ${alpha('#36ADF9', 0.4)}`,
    borderColor: alpha('#36ADF9', 0.4),
  },
  inspected: {
    main: '#FB619D',
    text: '#FB619D',
    bg: alpha('#FB619D', 0.05),
    border: '1px solid #FB5E9A',
    borderColor: '#FB5E9A',
  },
  stopSale: {
    main: grey[500],
    text: grey[600],
    bg: alpha('#D4CBCB', 0.2),
    border: `1px solid ${grey[300]}`,
    borderColor: grey[300],
  },
};

export const darkPms: PmsTokens = {
  stay: darkStay,
  dayUse: darkDayUse,
  reservationStatus: {
    requested: {
      main: '#FFAB00',
      text: '#FFD666',
      bg: alpha('#FFAB00', 0.16),
      border: `1px solid ${alpha('#FFAB00', 0.28)}`,
      borderColor: alpha('#FFAB00', 0.28),
    },
    checkedIn: {
      main: '#22C55E',
      text: '#77ED8B',
      bg: alpha('#22C55E', 0.16),
      border: `1px solid ${alpha('#22C55E', 0.28)}`,
      borderColor: alpha('#22C55E', 0.28),
    },
    checkedOut: {
      main: grey[500],
      text: grey[300],
      bg: alpha(grey[500], 0.16),
      border: `1px solid ${alpha(grey[500], 0.24)}`,
      borderColor: alpha(grey[500], 0.24),
    },
    canceled: {
      main: '#FF5630',
      text: '#FFAC82',
      bg: alpha('#FF5630', 0.16),
      border: `1px solid ${alpha('#FF5630', 0.28)}`,
      borderColor: alpha('#FF5630', 0.28),
    },
    noShow: {
      main: grey[500],
      text: grey[400],
      bg: alpha(grey[500], 0.16),
      border: `1px solid ${alpha(grey[500], 0.24)}`,
      borderColor: alpha(grey[500], 0.24),
    },
  },
  unassigned: {
    main: '#FF5730',
    text: '#FF8566',
    bg: alpha('#FF5730', 0.2),
    border: `1px solid ${alpha('#FF5730', 0.3)}`,
    borderColor: alpha('#FF5730', 0.3),
  },
  unclassified: {
    main: '#FF3A1F',
    text: '#FF7862',
    bg: alpha('#FF3A1F', 0.16),
    border: `1px solid ${alpha('#FF3A1F', 0.28)}`,
    borderColor: alpha('#FF3A1F', 0.28),
  },
  housekeeping: {
    main: '#36ADF9',
    text: '#66C2FF',
    bg: alpha('#36ADF9', 0.14),
    border: `1px solid ${alpha('#36ADF9', 0.4)}`,
    borderColor: alpha('#36ADF9', 0.4),
  },
  inspected: {
    main: '#FB619D',
    text: '#FF85B2',
    bg: alpha('#FB619D', 0.14),
    border: `1px solid ${alpha('#FB619D', 0.4)}`,
    borderColor: alpha('#FB619D', 0.4),
  },
  stopSale: {
    main: grey[400],
    text: grey[400],
    bg: alpha(grey[500], 0.16),
    border: `1px solid ${alpha(grey[500], 0.28)}`,
    borderColor: alpha(grey[500], 0.28),
  },
};

export function getPmsTokens(themeOrMode?: { palette?: { mode?: 'light' | 'dark'; pms?: PmsTokens } } | 'light' | 'dark'): PmsTokens {
  const mode = typeof themeOrMode === 'string' ? themeOrMode : themeOrMode?.palette?.mode ?? 'light';
  return mode === 'dark' ? darkPms : lightPms;
}

export function getReservationStatusThemeColor(
  status: string | undefined | null,
  themeOrMode?: { palette?: { mode?: 'light' | 'dark'; pms?: PmsTokens } } | 'light' | 'dark'
): StatusColorToken {
  const tokens = getPmsTokens(themeOrMode);
  const isDark = (typeof themeOrMode === 'string' ? themeOrMode : themeOrMode?.palette?.mode) === 'dark';

  switch (status) {
    case 'REQUESTED':
      return tokens.reservationStatus.requested;
    case 'CHECKED_IN':
      return tokens.reservationStatus.checkedIn;
    case 'CHECKED_OUT':
      return tokens.reservationStatus.checkedOut;
    case 'CANCELED_AFTER_REQUESTED':
    case 'CANCELED_AFTER_CHECKED_IN':
      return tokens.reservationStatus.canceled;
    case 'NO_SHOW':
      return tokens.reservationStatus.noShow;
    default:
      return {
        main: grey[500],
        text: isDark ? grey[400] : grey[600],
        bg: isDark ? alpha(grey[500], 0.16) : alpha(grey[500], 0.08),
        border: isDark ? `1px solid ${alpha(grey[500], 0.24)}` : 'none',
        borderColor: isDark ? alpha(grey[500], 0.24) : 'transparent',
      };
  }
}

export function getRoomTypeThemeColor(
  roomType: string | undefined | null,
  themeOrMode?: { palette?: { mode?: 'light' | 'dark'; pms?: PmsTokens } } | 'light' | 'dark'
): DomainColorToken {
  const tokens = getPmsTokens(themeOrMode);
  return roomType === 'DAY_USE' ? tokens.dayUse : tokens.stay;
}

// ----------------------------------------------------------------------

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...base,
    mode: 'light',
    stay: lightStay,
    dayUse: lightDayUse,
    housekeeping: {
      ...housekeeping,
      ...lightPms.housekeeping,
    },
    inspected: {
      ...inspected,
      ...lightPms.inspected,
    },
    pms: lightPms,
    text: {
      primary: grey[800],
      secondary: grey[600],
      disabled: grey[500],
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: grey[200],
    },
    action: {
      ...base.action,
      active: grey[600],
    },
  };

  const dark = {
    ...base,
    mode: 'dark',
    stay: darkStay,
    dayUse: darkDayUse,
    housekeeping: {
      ...housekeeping,
      ...darkPms.housekeeping,
    },
    inspected: {
      ...inspected,
      ...darkPms.inspected,
    },
    pms: darkPms,
    text: {
      primary: '#FFFFFF',
      secondary: grey[400],
      disabled: grey[500],
    },
    background: {
      paper: grey[800],
      default: grey[900],
      neutral: alpha(grey[500], 0.12),
    },
    action: {
      hover: alpha(grey[500], 0.12),
      selected: alpha(grey[500], 0.24),
      disabled: alpha(grey[500], 0.6),
      disabledBackground: alpha(grey[500], 0.24),
      focus: alpha(grey[500], 0.24),
      hoverOpacity: 0.12,
      disabledOpacity: 0.48,
      active: grey[500],
    },
    divider: alpha(grey[500], 0.24),
  };

  return mode === 'light' ? light : dark;
}
